import { useEffect, useState } from 'react';

import App, { type AppProps } from './App';
import { EmbeddedModeContext } from './contexts/EmbeddedModeContext';
import { MOCKS_ENABLED } from './env';
import { ModuleProviders } from './federation/ModuleProviders';

import type { SessionUser } from './types/session/SessionUser';

// ARCHITECTURE INVARIANT — DO NOT add `import './app.css'` here.
// This file is exposed via Module Federation as './App'. Any CSS import here

// Re-exported for the `src/api` modules that already import it from here. The client itself now
// belongs to `@/api/queryClient`, which is where new callers should read it.
export { queryClient } from './api/queryClient';

export interface AppWithProvidersProps extends AppProps {
    /** Declared by every entry point; `ModuleProviders` hands it to the tree. */
    user?: SessionUser;
    portalTarget?: HTMLElement | null;
    toastTarget?: HTMLElement | null;
}

/**
 * App wrapper with all necessary providers
 * Used by Shadow DOM / Module Federation
 */
export default function AppWithProviders({
    language,
    portalTarget,
    user,
    toastTarget,
    embedded = false,
}: AppWithProvidersProps) {
    const isTestMode = import.meta.env.MODE === 'test';
    const [mocksReady, setMocksReady] = useState(isTestMode || !MOCKS_ENABLED);

    useEffect(() => {
        if (!MOCKS_ENABLED || isTestMode) {
            return;
        }

        import('../mocks/browser').then(({ worker }) => {
            worker.start().then(() => {
                setMocksReady(true);
            });
        });
    }, [isTestMode]);

    if (!mocksReady) {
        return null;
    }

    return (
        <ModuleProviders
            user={user}
            portalTarget={portalTarget}
            toastTarget={toastTarget}
        >
            <EmbeddedModeContext.Provider value={embedded}>
                <App language={language} />
            </EmbeddedModeContext.Provider>
        </ModuleProviders>
    );
}
